import { create } from "zustand";

export type ThemePref = "light" | "dark" | "system";
export type Resolved = "light" | "dark";

const STORAGE_KEY = "atelier.theme";

function systemDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(pref: ThemePref): Resolved {
  if (pref === "system") return systemDark() ? "dark" : "light";
  return pref;
}

function applyClass(resolved: Resolved): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document.documentElement.style.colorScheme = resolved;
}

function loadInitial(): ThemePref {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  return "system";
}

const initial = loadInitial();
applyClass(resolve(initial));

interface ThemeStore {
  pref: ThemePref;
  resolved: Resolved;
  setPref: (p: ThemePref) => void;
}

export const useThemeStore = create<ThemeStore>((set) => ({
  pref: initial,
  resolved: resolve(initial),
  setPref: (pref) => {
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, pref);
    const resolved = resolve(pref);
    applyClass(resolved);
    set({ pref, resolved });
  },
}));

// React to OS changes only when the user hasn't picked a hard preference.
if (typeof window !== "undefined") {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    const { pref } = useThemeStore.getState();
    if (pref === "system") {
      const resolved = resolve(pref);
      applyClass(resolved);
      useThemeStore.setState({ resolved });
    }
  };
  if (mq.addEventListener) mq.addEventListener("change", onChange);
  else if (mq.addListener) mq.addListener(onChange);
}
